const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

class SanneverseOverlays extends HTMLElement {
  connectedCallback() {
    this.panels = new Map();
    for (const panel of this.querySelectorAll('[data-sv-overlay]')) {
      this.panels.set(panel.dataset.svOverlay, panel);
      panel.inert = true;
    }
    if (!this.panels.size) return;

    this.active = null;
    this.opener = null;

    this.onDocClick = this.onDocClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.active) this.close();
  }

  onDocClick(event) {
    const opener = event.target.closest('[data-sv-overlay-open]');
    if (opener && this.panels.has(opener.dataset.svOverlayOpen)) {
      event.preventDefault();
      this.open(opener.dataset.svOverlayOpen, opener);
      return;
    }

    if (!this.active) return;

    const tab = event.target.closest('[data-sv-overlay-switch]');
    if (tab && this.active.contains(tab)) {
      event.preventDefault();
      const target = tab.dataset.svOverlaySwitch;
      if (this.panels.has(target) && target !== this.active.dataset.svOverlay) this.open(target, this.opener);
      return;
    }

    if (event.target.closest('[data-sv-overlay-close]') && this.active.contains(event.target)) {
      event.preventDefault();
      this.close();
    }
  }

  onKeydown(event) {
    if (!this.active) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trap(event);
  }

  trap(event) {
    const items = [...this.active.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open(id, opener) {
    const panel = this.panels.get(id);
    if (!panel || this.active === panel) return;

    const previous = this.active;
    if (!previous) {
      this.opener = opener || null;
      this.lockScroll();
    }

    this.reveal(panel);
    if (previous) this.hide(previous);

    this.active = panel;
    const focusTarget = panel.querySelector('[data-sv-overlay-close]') || panel;
    focusTarget.focus({ preventScroll: true });
  }

  close() {
    if (!this.active) return;
    this.hide(this.active);
    this.active = null;
    this.unlockScroll();
    if (this.opener) {
      this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }

  reveal(panel) {
    panel.inert = false;
    this.mountMedia(panel);
    panel.classList.add('is-open');
    panel.scrollTop = 0;
  }

  hide(panel) {
    panel.classList.remove('is-open');
    panel.inert = true;
    this.unmountMedia(panel);
  }

  mountMedia(panel) {
    for (const slot of panel.querySelectorAll('[data-sv-media]')) {
      const template = slot.querySelector('template');
      if (!template || slot.childElementCount > 1) continue;
      slot.append(template.content.cloneNode(true));
    }
  }

  unmountMedia(panel) {
    for (const slot of panel.querySelectorAll('[data-sv-media]')) {
      const template = slot.querySelector('template');
      slot.replaceChildren();
      if (template) slot.append(template);
    }
  }

  lockScroll() {
    const root = document.documentElement;
    if (root.dataset.svOverlayLock) return;
    const gutter = window.innerWidth - root.clientWidth;
    root.dataset.svOverlayLock = '1';
    root.style.overflow = 'hidden';
    if (gutter > 0) root.style.paddingRight = `${gutter}px`;
  }

  unlockScroll() {
    const root = document.documentElement;
    if (!root.dataset.svOverlayLock) return;
    delete root.dataset.svOverlayLock;
    root.style.overflow = '';
    root.style.paddingRight = '';
  }
}

if (!customElements.get('sanneverse-overlays')) {
  customElements.define('sanneverse-overlays', SanneverseOverlays);
}
